<script setup lang="ts">
import { ref, computed, onMounted, watch, onUnmounted } from 'vue';
import CalendarDay from '../components/CalendarDay.vue';
import OrderPageTab from '@/corePagesModule/components/OrderPageTab.vue';
import NumberInput from '../components/NumberInput.vue';
import BulkOrderSelectInput from '../components/BulkOrderSelectInput.vue';
import { useOrderCalendarStore } from './orderCalendarStore';
import { storeToRefs } from 'pinia';
import { DateService } from '@/coreDomainsModule/services/dateService';
import SvgLoader from '@/utils/SvgLoader.vue';
import DayModal from '../components/DayModal.vue';
import { CalendarValidationService } from '../domains/services/CalendarValidationService';
import { onBeforeRouteLeave } from 'vue-router';
import { i18n } from '@/i18n';
import { OrderRepository } from '@/coreInfrastructureModule/Repositories/OrderRepository';
import MultipleSelectInput from '@/corePagesModule/SelectInput/MultipleSelectInput.vue';
import GroupListMultipleSelectInput from '@/corePagesModule/group/components/GroupListMultipleSelectInput.vue';
import InfoModal from '@/corePagesModule/components/InfoModal.vue';
import { waitLoading } from '@/corePagesModule/components/InfoModal/waitLoading';
import FilterModalButton from '@/corePagesModule/filterModal/FilterModalButton.vue';

const t = i18n.t;

const orderRepository = new OrderRepository();
const dayNames = [t('曜日:日'), t('曜日:月'), t('曜日:火'), t('曜日:水'), t('曜日:木'), t('曜日:金'), t('曜日:土')];
const orderCalendarStore = useOrderCalendarStore();
const { state } = storeToRefs(orderCalendarStore);
const { fetchOrders, getOrdersByDate, onSelectedGroup, onAppliedFilterModal, getFilterItemsValue } = orderCalendarStore;

const isOpenSaveSuccessModal = ref(false);
const isOpenSaveErrorModal = ref(false);

onMounted(async () => {
  await fetchOrders(orderRepository);
});

onUnmounted(() => {});

onBeforeRouteLeave((to, from, next) => {
  orderCalendarStore.$reset();
  orderCalendarStore.$dispose();
  next(); // 遷移を続行
});

/**
 * 指定された日付が属する月のカレンダーデータを生成する。
 * @param date - 特定の月を表すDateオブジェクト。
 * @returns 特定の月のカレンダーデータを含む週の配列。各日付は当月のものか、週末かどうかの情報を含む。
 */
const getMonthData = (date: Date) => {
  const result: Array<Array<{ date: Date; isWeekend: boolean; isCurrentMonth: boolean }>> = [];
  const month = date.getMonth();
  const year = date.getFullYear();

  // 月の最初の日
  const firstDayOfMonth = new Date(year, month, 1);
  // 最初の日が属する週の日曜日
  const startDay = new Date(firstDayOfMonth);
  startDay.setDate(firstDayOfMonth.getDate() - firstDayOfMonth.getDay());

  // 月の最後の日
  const lastDayOfMonth = new Date(year, month + 1, 0);
  // 最後の日が属する週の土曜日（6週間後の土曜日に修正）
  const endDay = new Date(lastDayOfMonth);
  const daysToAdd = 5 * 7 - lastDayOfMonth.getDay();
  endDay.setDate(lastDayOfMonth.getDate() + daysToAdd);

  let dateCursor = new Date(startDay);
  while (dateCursor <= endDay) {
    const week: Array<{ date: Date; isWeekend: boolean; isCurrentMonth: boolean }> = [];
    for (let day = 0; day < 7; day++) {
      week.push({
        date: new Date(dateCursor),
        isWeekend: dateCursor.getDay() === 0 || dateCursor.getDay() === 6,
        isCurrentMonth: dateCursor.getMonth() === month,
      });
      dateCursor.setDate(dateCursor.getDate() + 1);
    }
    result.push(week);
  }

  return result;
};

/**
 * 前の月に移動する関数
 */
const goToPreviousMonth = () => {
  const newDate = DateService.getShiftedDateByMonths(state.value.currentMonthDate, -1);
  state.value.currentMonthDate = newDate; // 新しいDateインスタンスを代入
  fetchOrders(orderRepository);
};

/**
 * 次の月に移動する関数
 */
const goToNextMonth = () => {
  const newDate = DateService.getShiftedDateByMonths(state.value.currentMonthDate, 1);
  state.value.currentMonthDate = newDate; // 新しいDateインスタンスを代入
  fetchOrders(orderRepository);
};

/**
 * 日付モーダルの保存後の処理
 * @param isSuccess - 保存が成功したかどうか
 */
const onDayModalSaved = async (isSuccess: boolean) => {
  await waitLoading(fetchOrders(orderRepository));
  if (isSuccess) {
    // 成功した旨のモーダルを開く
    isOpenSaveSuccessModal.value = true;
  } else {
    // 失敗した旨のモーダルを開く
    isOpenSaveErrorModal.value = true;
  }
};

/**
 * 保存成功モーダルを閉じる
 */
const closeSaveSuccessModal = () => {
  isOpenSaveSuccessModal.value = false;
};

/**
 * 保存失敗モーダルを閉じる
 */
const closeSaveErrorModal = () => {
  isOpenSaveErrorModal.value = false;
};

const weeks = computed(() => getMonthData(state.value.currentMonthDate));
const currentMonth = computed(() => state.value.currentMonthDate.getMonth());
const currentYear = computed(() => state.value.currentMonthDate.getFullYear());
const unit = computed(() => state.value.allOrders[0]?.unit ?? '');

// watch(
//   () => state.value.selectedInfo.warehouseName,
//   async () => {
//     await simulateInventory(organizationBusinessRepository, { year: currentYear.value, month: currentMonth.value + 1 });
//   }
// );

// watch(
//   () => currentMonth.value,
//   async () => {
//     await simulateInventory(organizationBusinessRepository, { year: currentYear.value, month: currentMonth.value + 1 });
//   }
// );
</script>

<template>
  <!-- 発注カレンダーページ -->
  <div class="calendar">
    <!-- タイトル -->
    <h1 class="calendar__title">{{ t('発注') }}</h1>

    <!-- ページの選択系&フィルターボタンの行 -->
    <div class="calendar__select-page-and-filter-button">
      <!-- ページ遷移タブ -->
      <OrderPageTab class="" />
      <!-- フィルターボタン -->
      <FilterModalButton
        class="detail-filter-modal"
        :filter-modal-type="'order-calendar'"
        :select-input-index="0"
        :filter-items="getFilterItemsValue().value"
        @apply="(filteredContents) => onAppliedFilterModal(orderRepository, filteredContents)"
      />
    </div>

    <!-- 選択系の行 -->
    <div class="calendar__select-container-group">
      <div class="calendar__select-container calendar__select-container--warehouse">
        <!-- 置き場名 -->
        <MultipleSelectInput
          :index="20"
          :selected-list="state.selectedInfo.warehouseName"
          :placeholder="t('置場名')"
          :label="t('置場名')"
          :filter-type="'WAREHOUSE'"
          :is-open-prop="state.filterInfo.isOpenFilterType === 'WAREHOUSE'"
          use-teleport
          @click:select-input="() => (state.filterInfo.isOpenFilterType = 'WAREHOUSE')"
          @update:model-value="(e) => (state.selectedInfo.warehouseName = e)"
          @close="fetchOrders(orderRepository)"
        />
        <!-- 納入上限数量 -->
        <NumberInput
          :model-value="state.selectedInfo.dailyDeliveryLimit"
          :index="21"
          :placeholder="t('1日の納入上限数量')"
          :label="t('1日の納入上限数量')"
          :disabled="state.selectedInfo.warehouseName.length === 0"
          @update:model-value="(e) => (state.selectedInfo.dailyDeliveryLimit = e)"
        />
      </div>
      <div class="calendar__select-container calendar__select-container--supplier">
        <!-- グループ -->
        <GroupListMultipleSelectInput
          :select-input-index="22"
          :store-tag="'orderCalendar'"
          :selected-group-ids-prop="state.selectedInfo.groupId"
          :show-label="true"
          :multi-selectable="true"
          use-teleport
          @selected-groups="(groupIds) => onSelectedGroup(orderRepository, groupIds)"
        />
        <!-- まとめ発注 -->
        <BulkOrderSelectInput
          :model-value="state.selectedInfo.bulkOrder"
          :index="23"
          :placeholder="t('まとめ発注')"
          :disabled="state.selectedInfo.groupId.length === 0"
          @update:model-value="(e) => (state.selectedInfo.bulkOrder = e)"
        />
      </div>
    </div>

    <!-- カレンダーの操作と情報の行 -->
    <div class="calendar__info-container">
      <!-- カレンダーの月を操作する -->
      <div class="calendar__info-month-selector">
        <span class="calendar__info-month-selector-text">
          {{ t('発注カレンダー(表示年月切り替え)', { year: currentYear, month: currentMonth + 1 }) }}
        </span>
        <button
          :disabled="!CalendarValidationService.isCanMovePreviousMonth(new Date(currentYear, currentMonth))"
          class="calendar__info-month-selector-button"
          @click="goToPreviousMonth"
        >
          <SvgLoader name="arrowLeft" />
        </button>
        <button
          :disabled="!CalendarValidationService.isCanMoveNextMonth(new Date(currentYear, currentMonth))"
          class="calendar__info-month-selector-button"
          @click="goToNextMonth"
        >
          <SvgLoader name="arrowRight" />
        </button>
      </div>
      <div class="calendar__info-delivery-date">
        <div class="calendar__info-delivery-date-confirm">{{ t('納期確定') }}</div>
        <div class="calendar__info-delivery-date-pending">{{ t('納期未確定') }}</div>
        <div class="calendar__info-delivery-date-text">{{ t('納期が未確定の発注はドラッグ&ドロップで移動可能です。') }}</div>
      </div>
    </div>

    <!-- カレンダー -->
    <div class="calendar__main">
      <div class="calendar__main-header">
        <div
          v-for="dayName in dayNames"
          :key="dayName"
          :class="`calendar__main-header-text ${
            [t('曜日:土'), t('曜日:日')].includes(dayName) ? 'calendar__main-header-text--weekend' : ''
          }`"
        >
          {{ t(dayName) }}
        </div>
      </div>
      <div class="calendar__main-body">
        <div v-for="week in weeks.slice(0, 6)" :key="week[0].date.toDateString()" class="calendar__main-body-week">
          <CalendarDay
            v-for="day in week"
            :key="day.date.toDateString()"
            :date="DateService.convertDateToIDate(day.date)"
            :is-weekend="day.isWeekend || DateService.isHoliday(day.date)"
            :orders="getOrdersByDate(DateService.convertDateToIDate(day.date))"
          />
        </div>
      </div>
    </div>
    <DayModal @on-saved="onDayModalSaved" />
    <InfoModal
      :modal-state="isOpenSaveSuccessModal"
      icon="info"
      :back-button-label="t('閉じる')"
      @on-back="closeSaveSuccessModal"
      @outside-click="closeSaveSuccessModal"
    >
      <template #title>{{ t('変更に成功しました。') }}</template>
    </InfoModal>
    <InfoModal
      :modal-state="isOpenSaveErrorModal"
      icon="error"
      color-style="error"
      :back-button-label="t('確認')"
      @on-back="closeSaveErrorModal"
      @outside-click="closeSaveErrorModal"
    >
      <template #title>{{ t('登録に失敗しました。') }}<br />{{ t('時間をおいて再度登録をお願いします。') }}</template>
    </InfoModal>
  </div>
</template>

<style scoped lang="scss">
.calendar {
  display: grid;
  gap: 16px;
  margin: 16px;

  &__select-container {
    display: grid;
    gap: 8px;
    align-items: start;

    &--warehouse {
      grid-template-columns: minmax(0, 200px) minmax(0, 160px);
    }

    &--supplier {
      grid-template-columns: repeat(2, minmax(0, 160px));
    }

    &-group {
      display: grid;
      justify-content: start;
      gap: 24px;
      grid-template-columns: repeat(2, auto);
    }
  }

  &__title {
    font-family: Noto Sans JP;
    font-size: 22px;
    font-weight: 700;
    line-height: 32px;
    letter-spacing: 0em;
    text-align: left;
    color: $textDefault;
  }

  &__select-page-and-filter-button {
    display: grid;
    grid-template-columns: minmax(0, 142px) minmax(0, 180px);
    align-items: center;
    gap: 8px;
  }

  &__info {
    &-container {
      display: grid;
      gap: 16px;
      grid-template-columns: repeat(3, auto);
      align-items: center;
      justify-content: start;
    }
    &-month-selector {
      display: grid;
      gap: 16px;
      grid-template-columns: 1fr repeat(2, 24px);
      align-items: center;
      padding: 4px 0;
      padding-right: 3px;
      background-color: $semanticWhite;

      &-text {
        font-family: Noto Sans JP;
        font-size: 16px;
        font-weight: 700;
        line-height: 16px;
        letter-spacing: 0em;
        text-align: center;
        padding: 6px 16px;
        color: $textDefault;
      }

      &-button {
        display: grid;
        align-content: center;
        justify-content: center;
        height: 24px;
        width: 24px;

        &:disabled {
          cursor: not-allowed;
          opacity: 0.5;
        }
      }
    }

    &-inventory {
      background-color: $surfaceDefault;
      display: grid;
      text-align: center;
      justify-content: start;
      gap: 24px;
      padding: 8px;
      grid-template-columns: repeat(4, auto);

      &-text {
        font-family: Noto Sans JP;
        font-size: 14px;
        font-weight: 400;
        line-height: 20px;
        letter-spacing: 0em;
        text-align: left;
        color: $textSecondary;

        &--bold {
          font-weight: 700;
        }
      }
    }

    &-delivery-date {
      display: grid;
      gap: 8px;
      align-items: center;
      justify-content: start;
      grid-template-columns: repeat(3, auto);

      &-confirm {
        font-family: Noto Sans JP;
        font-size: 12px;
        font-weight: 700;
        line-height: 12px;
        letter-spacing: 0.4;
        text-align: left;

        color: $semanticSuccess;

        padding: 4px 8px;
      }

      &-pending {
        font-family: Noto Sans JP;
        font-size: 12px;
        font-weight: 700;
        line-height: 12px;
        letter-spacing: 0.4;
        text-align: left;

        color: $textSecondary;
        background-color: $semanticWhite;

        padding: 3px 7px;
        border: 1px solid $borderSecondary;
        border-radius: 4px;
      }

      &-text {
        font-family: Noto Sans JP;
        font-size: 14px;
        font-weight: 400;
        line-height: 20px;
        letter-spacing: 0em;
        text-align: left;

        color: $textSecondary;
      }
    }
  }

  &__main {
    margin-bottom: 16px;
    border: 1px solid $borderDefault;
    &-header {
      display: grid;
      grid-template-columns: repeat(7, 1fr);
      background-color: $semanticWhite;
      border: 1px solid $borderDefault;

      &-text {
        font-family: Noto Sans JP;
        font-size: 16px;
        font-weight: 700;
        line-height: 16px;
        letter-spacing: 0em;
        text-align: center;
        color: $textSecondary;
        padding: 8px 0;

        &--weekend {
          color: $optionalPink;
        }
      }
    }

    &-body {
      display: grid;
      grid-template-rows: repeat(6, 1fr);

      &-week {
        display: grid;
        grid-template-columns: repeat(7, 1fr);
      }
    }
  }
}
</style>
